'use client';

import * as React from 'react';
import cx from 'classnames';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

import { Typography } from '@components/typography';

import { sectionHeadingClassName } from './styles';

export interface Faq {
  question: string;
  answer: React.ReactNode;
}

interface FaqsSectionProps {
  faqs: readonly Faq[];
  /** Rendered under the list, e.g. a link to the full help center. */
  children?: React.ReactNode;
}

export const FaqsSection: React.FC<FaqsSectionProps> = ({ faqs, children }) => (
  <section className="w-full bg-white">
    <div className="mx-auto flex w-full max-w-[950px] flex-col items-center px-4 py-16 text-primary-main sm:px-8 sm:py-20">
      <Typography
        variant="title4"
        component="h2"
        className={cx('text-center', sectionHeadingClassName)}
      >
        Questions people actually ask
      </Typography>

      <div className="mt-8 flex w-full flex-col gap-4 sm:mt-12">
        {faqs.map((item) => (
          <Disclosure
            key={item.question}
            as="div"
            className="rounded-lg bg-off-white"
          >
            {({ open }) => (
              <>
                <Typography component="h3" variant="body1">
                  <DisclosureButton className="flex w-full items-center justify-between gap-4 rounded-lg p-4 text-left !text-lg !font-semibold !leading-7 text-primary-main focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-main">
                    {item.question}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={cx(
                        'h-4 w-4 flex-shrink-0 text-primary-main transition-transform duration-300',
                        { 'rotate-180': open }
                      )}
                    />
                  </DisclosureButton>
                </Typography>
                <DisclosurePanel unmount={false}>
                  <Typography
                    component="p"
                    variant="body1"
                    className="px-4 pb-4 !text-base !leading-6 text-primary-main"
                  >
                    {item.answer}
                  </Typography>
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        ))}
      </div>

      {children}
    </div>
  </section>
);
